import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { X, Minus, Plus, Trash2, ShoppingBag, LoaderCircle } from "lucide-react";
import { useCartStore, isValidPromoCode, type CartItem } from "../store/cartStore";
import { api, apiUrls } from "../utils/api";
import { trackEvent } from "../utils/trackEvent";
import { routes } from "../routes";
import { ShippingSelector } from "./ShippingSelector";
import { STATE_REQUIRED_COUNTRIES } from "../data/shippingCountries";

const TRANSITION_MS = 300;

function itemLabel(item: CartItem) {
  if (item.content?.name) return item.content.name;
  if (item.kind === "posterTemplate") return item.templateSlug;
  return "Item";
}

function itemSize(item: CartItem) {
  if (item.kind === "posterTemplate") return item.selectedSizeCode;
  return null;
}

export function CartDrawer() {
  const navigate = useNavigate();
  const open = useCartStore((s) => s.drawerOpen);
  const close = useCartStore((s) => s.closeDrawer);
  const items = useCartStore((s) => s.items);
  const removeItem = useCartStore((s) => s.removeItem);
  const updateQty = useCartStore((s) => s.updateQty);
  const clearCart = useCartStore((s) => s.clearCart);
  const subtotal = useCartStore((s) => s.subtotal);
  const promoCode = useCartStore((s) => s.promoCode);
  const discountAmount = useCartStore((s) => s.discountAmount);
  const discountedSubtotal = useCartStore((s) => s.discountedSubtotal);
  const address = useCartStore((s) => s.address);
  const gift = useCartStore((s) => s.gift);
  const setPromoCode = useCartStore((s) => s.setPromoCode);

  const [checkingOut, setCheckingOut] = useState(false);
  const [promoInput, setPromoInput] = useState(promoCode ?? "");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [shippingCents, setShippingCents] = useState<number | null>(null);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Animation state: mounted keeps DOM alive during exit transition
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), TRANSITION_MS);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const hasShippableItems = items.some(
    (i) => i.kind === "merch" || i.kind === "posterTemplate",
  );
  const needsState = STATE_REQUIRED_COUNTRIES.has(
    (address?.country ?? "") as any,
  );
  const shippingReady =
    !hasShippableItems ||
    (!!address?.country && (!needsState || !!address?.state));

  const shippableQty = useMemo(
    () =>
      items
        .filter((i) => i.kind === "merch" || i.kind === "posterTemplate")
        .reduce((sum, i) => sum + i.qty, 0),
    [items],
  );

  // Fetch shipping estimate when address or shippable qty changes
  useEffect(() => {
    if (!hasShippableItems || !shippingReady || !shippableQty) {
      setShippingCents(null);
      return;
    }
    let cancelled = false;
    setLoadingShipping(true);
    api
      .post<{ shippingAmountCents: number }>(apiUrls.shippingEstimate, {
        country: address!.country,
        state: address!.state || undefined,
        qty: shippableQty,
      })
      .then((res) => {
        if (!cancelled) setShippingCents(res.data.shippingAmountCents);
      })
      .catch(() => {
        if (!cancelled) setShippingCents(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingShipping(false);
      });
    return () => {
      cancelled = true;
    };
  }, [address?.country, address?.state, shippableQty, hasShippableItems, shippingReady]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleCheckout = async () => {
    if (items.length === 0 || !shippingReady) return;
    setCheckingOut(true);
    try {
      const res = await api.post<{ url: string }>(apiUrls.createCheckoutSession, {
        cart: items,
        address: address ?? undefined,
        promoCode: promoCode ?? undefined,
        gift: gift?.enabled ? gift : undefined,
        cancelUrl: window.location.origin + window.location.pathname + window.location.search,
        successUrl: window.location.origin + routes.paymentSuccess,
      });
      trackEvent("Checkout Started", {
        itemCount: items.length,
        subtotal: subtotal(),
        promoCode,
      });
      window.location.href = res.data.url;
    } catch {
      setCheckingOut(false);
    }
  };

  const handleApplyPromo = () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoError(null);
    setPromoLoading(true);
    // Small delay so user sees loading state
    setTimeout(() => {
      if (isValidPromoCode(code)) {
        setPromoCode(code);
        setPromoInput("");
        trackEvent("Promo Code Applied", { code });
      } else {
        setPromoError("Invalid promo code");
      }
      setPromoLoading(false);
    }, 400);
  };

  if (!mounted) return null;

  const sub = subtotal();
  const disc = discountAmount();
  const total = discountedSubtotal();
  const shippingUsd = shippingCents != null ? shippingCents / 100 : 0;
  const grandTotal = total + shippingUsd;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        onClick={close}
      />

      {/* Drawer panel */}
      <div
        className={`relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl transition-transform duration-300 ease-out sm:max-w-lg ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-primary-400" />
            <h2 className="text-lg font-bold text-slate-900">
              Your Cart{" "}
              <span className="text-sm font-normal text-slate-400">
                ({items.length} {items.length === 1 ? "item" : "items"})
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ShoppingBag className="mb-4 h-12 w-12 text-slate-200" />
              <p className="text-sm text-slate-400">Your cart is empty</p>
              <button
                type="button"
                onClick={() => {
                  close();
                  navigate(routes.shop);
                }}
                className="mt-4 rounded-full bg-primary-400 px-5 py-2 text-sm font-semibold text-white"
              >
                Browse Posters
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.cartItemId}
                  className="flex gap-3 rounded-xl border border-slate-100 bg-white p-3"
                >
                  {/* Thumbnail */}
                  {item.content?.image && (
                    <img
                      src={item.content.image}
                      alt={itemLabel(item)}
                      className="h-20 w-16 shrink-0 rounded-lg border border-slate-100 object-cover"
                    />
                  )}

                  {/* Details */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {itemLabel(item)}
                    </p>
                    {itemSize(item) && (
                      <p className="mt-0.5 text-xs text-slate-400">
                        {itemSize(item)}
                      </p>
                    )}
                    <p className="mt-1 text-sm font-bold text-slate-900">
                      ${(item.price * item.qty).toFixed(2)}
                    </p>

                    {/* Qty controls */}
                    <div className="mt-2 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => updateQty(item.cartItemId, item.qty - 1)}
                        className="rounded-md border border-slate-200 p-1 text-slate-500"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="px-2 text-sm font-semibold text-slate-900">
                        {item.qty}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateQty(item.cartItemId, item.qty + 1)}
                        className="rounded-md border border-slate-200 p-1 text-slate-500"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(item.cartItemId)}
                        className="ml-auto rounded-md p-1 text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {item.kind === "posterTemplate" &&
                      item.personalizationUploadId && (
                        <p className="mt-1.5 border-t border-slate-50 pt-1.5 text-[10px] text-slate-300">
                          You'll personalize after payment
                        </p>
                      )}
                  </div>
                </div>
              ))}

              {/* Clear cart */}
              <button
                type="button"
                onClick={clearCart}
                className="text-xs font-semibold text-slate-400 hover:text-red-500"
              >
                Clear cart
              </button>
            </div>
          )}
        </div>

        {/* Footer — promo + totals + checkout */}
        {items.length > 0 && (
          <div className="border-t border-slate-100 px-5 py-4 space-y-4">
            {/* Shipping destination (shippable items only) */}
            {hasShippableItems && <ShippingSelector />}

            {/* Promo code */}
            <div className="flex gap-2">
              <input
                type="text"
                value={promoInput}
                onChange={(e) => {
                  setPromoInput(e.target.value);
                  if (promoError) setPromoError(null);
                }}
                placeholder="Promo code"
                className={`flex-1 rounded-lg border bg-slate-50 px-3 py-2 text-base text-slate-900 placeholder:text-slate-300 ${
                  promoError ? "border-red-400" : "border-slate-200"
                }`}
              />
              <button
                type="button"
                onClick={handleApplyPromo}
                disabled={promoLoading || !promoInput.trim()}
                className="flex items-center gap-1.5 rounded-lg border border-primary-400 px-4 py-2 text-sm font-semibold text-primary-400 disabled:opacity-50"
              >
                {promoLoading && <LoaderCircle className="h-3.5 w-3.5 animate-spin" />}
                Apply
              </button>
            </div>
            {promoError && (
              <p className="text-xs text-red-500">{promoError}</p>
            )}
            {promoCode && (
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-green-700">
                  {promoCode} applied
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setPromoCode(null);
                    setPromoInput("");
                  }}
                  className="text-slate-400 hover:text-red-500"
                >
                  Remove
                </button>
              </div>
            )}

            {/* Summary */}
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Subtotal</span>
                <span className="font-semibold text-slate-900">
                  ${sub.toFixed(2)}
                </span>
              </div>
              {disc > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Discount</span>
                  <span className="font-semibold">-${disc.toFixed(2)}</span>
                </div>
              )}
              {hasShippableItems && (
                <div className="flex justify-between text-slate-500">
                  <span>Shipping</span>
                  <span className="font-semibold text-slate-900">
                    {loadingShipping
                      ? "..."
                      : shippingCents != null
                        ? shippingCents === 0
                          ? "Free"
                          : `$${shippingUsd.toFixed(2)}`
                        : "—"}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t border-slate-100 pt-2 text-base font-bold text-slate-900">
                <span>Total</span>
                <span>${grandTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Checkout button */}
            <button
              type="button"
              onClick={handleCheckout}
              disabled={checkingOut || !shippingReady}
              className="flex h-13 w-full items-center justify-center gap-2 rounded-full bg-primary-400 text-base font-semibold text-white transition-colors hover:bg-primary-500 disabled:opacity-60"
            >
              {checkingOut ? (
                <>
                  <LoaderCircle className="h-5 w-5 animate-spin" />
                  Redirecting…
                </>
              ) : (
                "Proceed to Checkout"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
